import { useState, useEffect, useMemo } from "react";
import { DemoHint } from "@/components/demo/DemoHint";
import { useNavigate, useSearchParams } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useAuth } from "@/contexts/AuthContext";
import { useCompanyAccount } from "@/hooks/useCompanyAccount";
import { CreditProfileBanner } from "@/components/credit-profile/CreditProfileBanner";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Zap, Plus, ExternalLink, Eye, RotateCcw, Clock, ChevronDown, Copy, MapPin, Users, Globe, Settings, Pencil, Trash2, CheckCircle2, Printer, ClipboardList, BookUser, Truck, PackageCheck, Search as SearchIcon, ArrowUp, ArrowDown, ArrowUpDown, X, Archive, Undo2, ChevronLeft, ChevronRight, Radio, Trophy, FileText, Wrench, Info, Download } from "lucide-react";
import { ensureDispatch } from "@/hooks/useDirectDispatch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast as sonnerToast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { parseYYYYMMDDToLocalDate } from "@/lib/dateOnly";
import { formatEquipmentType, useEquipmentTypes } from "@/lib/equipmentTypes";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { isBiddingOpen, isDeadlinePassed } from "@/lib/lrDirect/directLoadStatus";
import { buildMyLoadsCsv, downloadCsv } from "@/lib/lrDirect/exportMyLoadsCsv";

interface DirectLoadStop {
  pickup_number: string | null;
  reference_numbers: string[] | null;
}

interface DirectLoad {
  id: string;
  load_number: string;
  origin_city: string;
  origin_state: string;
  destination_city: string;
  destination_state: string;
  equipment_type: string;
  pickup_date: string;
  status: string;
  bid_deadline: string | null;
  target_rate_min: number | null;
  target_rate_max: number | null;
  visibility_mode?: "open_market" | "private_only" | null;
  private_network_id?: string | null;
  awarded_bid_id?: string | null;
  archived_at?: string | null;
  load_type?: string | null;
  direct_load_stops?: DirectLoadStop[] | null;
}

type BidStats = Record<string, { bidCount: number; lowestBid: number | null; highestBid: number | null; sum: number }>;

type DispatchStatus =
  | "none" | "draft" | "ready" | "assigned"
  | "en_route_to_pickup" | "at_pickup" | "picked_up"
  | "en_route_to_delivery" | "at_delivery"
  | "in_transit" | "delivered" | "closed";

type DispatchInfo = { id: string | null; status: DispatchStatus };
type DispatchMap = Record<string, DispatchInfo>;

type Stage = "all" | "bidding" | "awarded" | "dispatching" | "in_transit" | "delivered" | "closed" | "archive";
type RealStage = Exclude<Stage, "all" | "archive">;
type SortKey =
  | "load_number" | "origin" | "destination" | "pickup_date"
  | "equipment_type" | "bid_count" | "lowest_bid" | "deadline" | "stage";
type SortDir = "asc" | "desc";

const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;

/**
 * Where a shipper's own load opens.
 *
 * Pre-award the destination is ALWAYS the Live Load Room (the bid room) —
 * that's where bids arrive and where awarding happens. Only once the load is
 * awarded does the tab page take over as the dispatch/documents/timeline
 * tracker. This page only ever renders the signed-in shipper's own loads,
 * so the bid room's owner-only gate is always satisfied here.
 */
function shipperLoadPath(load: DirectLoad, stage?: RealStage): string {
  const awarded =
    load.status === "awarded" ||
    !!load.awarded_bid_id ||
    stage === "awarded" ||
    stage === "dispatching" ||
    stage === "in_transit" ||
    stage === "delivered" ||
    stage === "closed";
  return awarded ? `/lr-direct/load/${load.id}` : `/lr-direct/live/${load.id}`;
}


const STAGE_RANK: Record<RealStage, number> = {
  bidding: 0, awarded: 1, dispatching: 2, in_transit: 3, delivered: 4, closed: 5,
};

const IN_TRANSIT_DISPATCH: DispatchStatus[] = [
  "en_route_to_pickup", "at_pickup", "picked_up",
  "en_route_to_delivery", "at_delivery", "in_transit",
];

function toYMD(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function deriveStage(load: { status: string; pickup_date: string; bid_deadline?: string | null }, dispatch: DispatchInfo, todayStr: string): RealStage {
  if (load.status === "expired" || load.status === "canceled" || load.status === "completed") return "closed";
  if (load.status === "posted") {
    // Deadline-aware: a posted load past its bid deadline is closed, even if
    // the scheduled expiry sweep hasn't flipped the status row yet.
    return isDeadlinePassed(load.bid_deadline) ? "closed" : "bidding";
  }
  // awarded
  const ds = dispatch.status;
  if (ds === "delivered" || ds === "closed") return "delivered";
  if (IN_TRANSIT_DISPATCH.includes(ds)) return "in_transit";
  // 'ready' = shipper explicitly clicked Mark Ready → Dispatching
  if (ds === "ready") return "dispatching";
  // 'assigned' is auto-set by award_direct_bid RPC, NOT a shipper action — treat as Awarded.
  // 'none' / 'draft' / 'assigned' all mean: shipper still needs to release the packet.
  return "awarded";
}

function isInProgress(load: { status: string; pickup_date: string }, todayStr: string): boolean {
  return load.status === "awarded" && load.pickup_date < todayStr;
}

function formatPickupLabel(dateStr: string): string {
  const d = parseYYYYMMDDToLocalDate(dateStr);
  if (!d) return dateStr;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const target = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  if (target.getTime() === today.getTime()) return "Today";
  if (target.getTime() === tomorrow.getTime()) return "Tomorrow";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function formatDeadline(deadline: string | null): { text: string; urgent: boolean } {
  if (!deadline) return { text: "—", urgent: false };
  const now = Date.now();
  const end = new Date(deadline).getTime();
  const diff = end - now;
  if (diff <= 0) return { text: "Closed", urgent: false };
  const hrs = Math.floor(diff / 3_600_000);
  const mins = Math.floor((diff % 3_600_000) / 60_000);
  if (hrs > 24) return { text: `${Math.floor(hrs / 24)}d ${hrs % 24}h`, urgent: false };
  if (hrs > 0) return { text: `${hrs}h ${mins}m`, urgent: hrs < 2 };
  return { text: `${mins}m`, urgent: true };
}

function getHealthIndicator(bidCount: number): { label: string; color: string; dotColor: string } {
  if (bidCount >= 3) return { label: "Strong", color: "text-emerald-500", dotColor: "bg-emerald-500" };
  if (bidCount >= 1) return { label: "Active", color: "text-amber-500", dotColor: "bg-amber-500" };
  return { label: "No Activity", color: "text-muted-foreground", dotColor: "bg-muted-foreground/40" };
}

export default function LRDirectMyOpportunitiesPage() {
  usePageMeta({ title: "LR Direct — My Loads | LaneRunner", description: "Manage your LR Direct loads." });

  const { user, profile } = useAuth();
  const { companyId, isAdmin } = useCompanyAccount();
  const isShipper = profile?.user_type === "shipper";
  const showCreditBanner = isShipper && !!companyId;
  const navigate = useNavigate();
  const { data: eqData } = useEquipmentTypes();
  const [loads, setLoads] = useState<DirectLoad[]>([]);
  const [bidStats, setBidStats] = useState<BidStats>({});
  const [dispatchMap, setDispatchMap] = useState<DispatchMap>({});
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const initialStage = (searchParams.get("stage") as Stage) || "all";
  const [filter, setFilter] = useState<Stage>(initialStage);
  const [reloadTick, setReloadTick] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(25);
  const isMobile = useIsMobile();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [bulkArchiveOpen, setBulkArchiveOpen] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  // Debounce search input → query (200ms)
  useEffect(() => {
    const t = setTimeout(() => setSearchQuery(searchInput.trim().toLowerCase()), 200);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Export ALL of the shipper's loads (full set, ignores search + stage tabs).
  // Data source is the RLS-scoped fetch above, so this can only ever contain
  // loads owned by this user / their company.
  const handleDownloadCsv = () => {
    if (loads.length === 0) return;
    const csv = buildMyLoadsCsv(
      loads as any,
      bidStats,
      (load: any) => load.id,
      (value) => (value ? formatEquipmentType(value, eqData) : ""),
    );
    downloadCsv(csv);
    sonnerToast.success(`Exported ${loads.length} load${loads.length === 1 ? "" : "s"} to CSV`);
  };

  const toggleSort = (key: SortKey) => {
    if (sortKey !== key) { setSortKey(key); setSortDir("asc"); return; }
    if (sortDir === "asc") { setSortDir("desc"); return; }
    setSortKey(null);
  };

  // Sync filter ↔ URL
  useEffect(() => {
    const cur = searchParams.get("stage");
    if (cur !== filter) {
      const next = new URLSearchParams(searchParams);
      next.set("stage", filter);
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  // Fetch loads + bid stats
  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      // Company-scoped ownership: a poster must see their own loads AND loads
      // posted by teammates on the same company account, in every status.
      let ownerIds = [user.id];
      if (companyId) {
        const { data: members } = await supabase
          .from("company_users")
          .select("user_id")
          .eq("company_id", companyId);
        const ids = new Set<string>([user.id]);
        (members ?? []).forEach((m: any) => m?.user_id && ids.add(m.user_id));
        ownerIds = Array.from(ids);
      }
      const { data: loadData } = await supabase
        .from("direct_loads" as any)
        .select("id, load_number, origin_city, origin_state, destination_city, destination_state, equipment_type, pickup_date, status, bid_deadline, target_rate_min, target_rate_max, visibility_mode, private_network_id, awarded_bid_id, archived_at, load_type, direct_load_stops(pickup_number, reference_numbers)")
        .in("created_by", ownerIds)
        .eq("post_type", "load")
        .is("deleted_at", null)
        .order("created_at", { ascending: false });

      if (cancelled) return;
      const rows = (loadData ?? []) as unknown as DirectLoad[];
      setLoads(rows);

      if (rows.length > 0) {
        const ids = rows.map((r) => r.id);
        const { data: bidData } = await supabase
          .from("direct_bids")
          .select("direct_load_id, bid_amount")
          .in("direct_load_id", ids)
          .in("status", ["pending", "accepted"]);

        if (!cancelled && bidData) {
          const map: BidStats = {};
          for (const b of bidData as any[]) {
            const key = b.direct_load_id as string;
            if (!map[key]) map[key] = { bidCount: 0, lowestBid: null, highestBid: null, sum: 0 };
            map[key].bidCount++;
            map[key].sum += Number(b.bid_amount) || 0;
            if (map[key].lowestBid === null || b.bid_amount < map[key].lowestBid!) {
              map[key].lowestBid = b.bid_amount;
            }
            if (map[key].highestBid === null || b.bid_amount > map[key].highestBid!) {
              map[key].highestBid = b.bid_amount;
            }
          }
          setBidStats(map);
        }

        // Fetch dispatch info alongside bid stats
        const { data: dispatches } = await supabase
          .from("direct_dispatches" as any)
          .select("id, direct_load_id, status")
          .in("direct_load_id", ids);
        if (!cancelled) {
          const dmap: DispatchMap = {};
          (dispatches as any[] | null ?? []).forEach((d) => {
            dmap[d.direct_load_id] = { id: d.id, status: d.status as DispatchStatus };
          });
          setDispatchMap(dmap);
        }
      }
      if (!cancelled) setLoading(false);
    };

    fetchData();
    return () => { cancelled = true; };
  }, [user, companyId, reloadTick]);

  // Refetch when tab becomes visible/focused so returning from Live Room after
  // awarding lights up "View Award" without a manual reload.
  useEffect(() => {
    const bump = () => setReloadTick((t) => t + 1);
    const onVis = () => { if (document.visibilityState === "visible") bump(); };
    window.addEventListener("focus", bump);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("focus", bump);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  // Realtime subscription for bid updates
  useEffect(() => {
    if (!user || loads.length === 0) return;
    const ids = loads.map((l) => l.id);
    // Track first SUBSCRIBED so self-heal only fires on RECONNECT — not on
    // the initial subscribe. Without this, SUBSCRIBED → setReloadTick →
    // new `loads` array reference → effect re-runs → new channel →
    // SUBSCRIBED again → infinite refetch loop, page stuck on skeleton.
    let hasSubscribedOnce = false;

    const channel = supabase
      .channel("shipper-portal-bids")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "direct_bids" }, (payload: any) => {
        const bid = payload.new;
        if (!ids.includes(bid.direct_load_id)) return;
        setBidStats((prev) => {
          const key = bid.direct_load_id as string;
          const existing = prev[key] || { bidCount: 0, lowestBid: null, highestBid: null, sum: 0 };
          return {
            ...prev,
            [key]: {
              bidCount: existing.bidCount + 1,
              lowestBid: existing.lowestBid === null || bid.bid_amount < existing.lowestBid ? bid.bid_amount : existing.lowestBid,
              highestBid: existing.highestBid === null || bid.bid_amount > existing.highestBid ? bid.bid_amount : existing.highestBid,
              sum: existing.sum + (Number(bid.bid_amount) || 0),
            },
          };
        });
      })
      .subscribe((status) => {
        if (status !== "SUBSCRIBED") return;
        if (!hasSubscribedOnce) { hasSubscribedOnce = true; return; }
        setReloadTick((t) => t + 1);
      });

    return () => { supabase.removeChannel(channel); };
  }, [user, loads]);

  const todayStr = useMemo(() => toYMD(new Date()), []);

  // Compute stage for each load
  const stagesById = useMemo(() => {
    const out: Record<string, RealStage> = {};
    for (const l of loads) {
      const d = dispatchMap[l.id] ?? { id: null, status: "none" as DispatchStatus };
      out[l.id] = deriveStage(l, d, todayStr);
    }
    return out;
  }, [loads, dispatchMap, todayStr]);

  // Archive-aware stage/search/sort filter.
  // Rule: archived loads are hidden from every tab EXCEPT the "archive" tab.
  // Exception: when there's an active search, results span archive so a
  // reference lookup can surface an archived load from anywhere.
  const filteredLoads = useMemo(() => {
    const hasSearch = !!searchQuery;

    // Archive scope
    const scopeFiltered = filter === "archive"
      ? loads.filter((l) => !!l.archived_at)
      : hasSearch
        ? loads // search spans archived rows too
        : loads.filter((l) => !l.archived_at);

    // Stage scope (skip on archive tab — archive shows any stage)
    const stageFiltered = (filter === "all" || filter === "archive")
      ? scopeFiltered
      : scopeFiltered.filter((l) => stagesById[l.id] === filter);

    const searched = hasSearch
      ? stageFiltered.filter((l) => {
          const hay: string[] = [
            l.load_number,
            l.origin_city, l.origin_state,
            l.destination_city, l.destination_state,
            l.equipment_type,
          ];
          for (const s of l.direct_load_stops ?? []) {
            if (s.pickup_number) hay.push(s.pickup_number);
            if (s.reference_numbers) hay.push(...s.reference_numbers);
          }
          return hay.some((v) => v && v.toLowerCase().includes(searchQuery));
        })
      : stageFiltered;

    if (!sortKey) return searched;
    const dir = sortDir === "asc" ? 1 : -1;
    const sorted = [...searched].sort((a, b) => {
      const sa = bidStats[a.id];
      const sb = bidStats[b.id];
      let av: string | number | null = null;
      let bv: string | number | null = null;
      switch (sortKey) {
        case "load_number": av = a.load_number; bv = b.load_number; break;
        case "origin": av = `${a.origin_state} ${a.origin_city}`; bv = `${b.origin_state} ${b.origin_city}`; break;
        case "destination": av = `${a.destination_state} ${a.destination_city}`; bv = `${b.destination_state} ${b.destination_city}`; break;
        case "pickup_date": av = a.pickup_date; bv = b.pickup_date; break;
        case "equipment_type": av = a.equipment_type; bv = b.equipment_type; break;
        case "bid_count": av = sa?.bidCount ?? 0; bv = sb?.bidCount ?? 0; break;
        case "lowest_bid": av = sa?.lowestBid ?? Number.POSITIVE_INFINITY; bv = sb?.lowestBid ?? Number.POSITIVE_INFINITY; break;
        case "deadline": av = a.bid_deadline ? new Date(a.bid_deadline).getTime() : Number.POSITIVE_INFINITY; bv = b.bid_deadline ? new Date(b.bid_deadline).getTime() : Number.POSITIVE_INFINITY; break;
        case "stage": av = STAGE_RANK[stagesById[a.id]] ?? 99; bv = STAGE_RANK[stagesById[b.id]] ?? 99; break;
      }
      if (av === null && bv === null) return 0;
      if (av === null) return 1;
      if (bv === null) return -1;
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
    return sorted;
  }, [loads, stagesById, filter, searchQuery, sortKey, sortDir, bidStats]);

  const totalRows = filteredLoads.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));

  // Reset to page 1 whenever the effective dataset changes.
  useEffect(() => { setPage(1); setSelectedIds(new Set()); }, [filter, searchQuery, sortKey, sortDir, pageSize]);
  // Clamp page if data shrinks (e.g. after archive/unarchive).
  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [page, totalPages]);

  const pagedLoads = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredLoads.slice(start, start + pageSize);
  }, [filteredLoads, page, pageSize]);

  // ─── Bulk selection helpers ──────────────────────────────────────────────
  const pageIds = useMemo(() => pagedLoads.map((l) => l.id), [pagedLoads]);
  const selectedOnPage = useMemo(
    () => pageIds.filter((id) => selectedIds.has(id)).length,
    [pageIds, selectedIds],
  );
  const pageAllSelected = pageIds.length > 0 && selectedOnPage === pageIds.length;
  const pageSomeSelected = selectedOnPage > 0 && !pageAllSelected;

  const toggleRow = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const togglePageAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (pageAllSelected) {
        pageIds.forEach((id) => next.delete(id));
      } else {
        pageIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  const selectedLoads = useMemo(
    () => loads.filter((l) => selectedIds.has(l.id)),
    [loads, selectedIds],
  );
  const selectedAwardedCount = useMemo(
    () => selectedLoads.filter((l) => l.status === "awarded" || !!l.awarded_bid_id).length,
    [selectedLoads],
  );
  const selectedAllArchived = selectedLoads.length > 0 && selectedLoads.every((l) => !!l.archived_at);
  const bulkDeleteDisabled = selectedAwardedCount > 0;
  const bulkDeleteTip = bulkDeleteDisabled
    ? `${selectedAwardedCount} of ${selectedLoads.length} selected ${selectedAwardedCount === 1 ? "is" : "are"} awarded — archive them instead.`
    : "Permanently remove selected loads.";

  const runBulk = async (
    action: "archive" | "unarchive" | "delete",
    onRowDone: (loadId: string, ok: boolean) => void,
  ) => {
    setBulkBusy(true);
    const targets = [...selectedLoads];
    const results = await Promise.allSettled(
      targets.map(async (l) => {
        if (action === "delete") {
          if (l.status === "awarded" || !!l.awarded_bid_id) {
            throw new Error("awarded loads can't be deleted");
          }
          const { data, error } = await supabase
            .from("direct_loads" as any)
            .update({ deleted_at: new Date().toISOString(), status: "canceled" })
            .eq("id", l.id)
            .select("id")
            .maybeSingle();
          if (error) throw new Error(error.message);
          if (!data) throw new Error("permission denied or already removed");
          // Best-effort cancel pending bids; don't block the aggregate on this.
          await supabase
            .from("direct_bids")
            .update({ status: "canceled" })
            .eq("direct_load_id", l.id)
            .eq("status", "pending");
          return l.id;
        }
        const nextArchivedAt = action === "archive" ? new Date().toISOString() : null;
        const { data, error } = await supabase
          .from("direct_loads" as any)
          .update({ archived_at: nextArchivedAt })
          .eq("id", l.id)
          .select("id, archived_at")
          .maybeSingle();
        if (error) throw new Error(error.message);
        if (!data) throw new Error("permission denied or already removed");
        return l.id;
      }),
    );

    const okIds: string[] = [];
    const failed: { load: DirectLoad; reason: string }[] = [];
    results.forEach((r, i) => {
      const load = targets[i];
      if (r.status === "fulfilled") {
        okIds.push(load.id);
        onRowDone(load.id, true);
      } else {
        failed.push({ load, reason: (r.reason as Error)?.message || "unknown error" });
        onRowDone(load.id, false);
      }
    });

    // Aggregate toast.
    if (failed.length === 0) {
      const verb = action === "delete" ? "deleted" : action === "archive" ? "archived" : "unarchived";
      sonnerToast.success(`${okIds.length} load${okIds.length === 1 ? "" : "s"} ${verb}`);
    } else if (okIds.length === 0) {
      sonnerToast.error(
        `Could not ${action} ${failed.length} load${failed.length === 1 ? "" : "s"}`,
        { description: failed.slice(0, 3).map((f) => `${f.load.load_number}: ${f.reason}`).join(" · ") },
      );
    } else {
      const verb = action === "delete" ? "deleted" : action === "archive" ? "archived" : "unarchived";
      sonnerToast.warning(
        `${okIds.length} ${verb} · ${failed.length} could not be ${verb}`,
        { description: failed.slice(0, 3).map((f) => `${f.load.load_number}: ${f.reason}`).join(" · ") },
      );
    }

    // Clear selection to just the ones that failed (so the user can retry).
    setSelectedIds(new Set(failed.map((f) => f.load.id)));
    setBulkBusy(false);
  };

  const executeBulkArchive = async (mode: "archive" | "unarchive") => {
    await runBulk(mode, (loadId, ok) => {
      if (!ok) return;
      const nextArchivedAt = mode === "archive" ? new Date().toISOString() : null;
      setLoads((prev) => prev.map((l) => l.id === loadId ? { ...l, archived_at: nextArchivedAt } : l));
    });
    setBulkArchiveOpen(false);
  };
  const executeBulkDelete = async () => {
    await runBulk("delete", (loadId, ok) => {
      if (!ok) return;
      setLoads((prev) => prev.filter((l) => l.id !== loadId));
    });
    setBulkDeleteOpen(false);
  };
  // ─────────────────────────────────────────────────────────────────────────

  const counts = useMemo(() => {
    const c: Record<Stage, number> = {
      all: 0, bidding: 0, awarded: 0, dispatching: 0, in_transit: 0, delivered: 0, closed: 0, archive: 0,
    };
    for (const l of loads) {
      if (l.archived_at) { c.archive++; continue; }
      c.all++;
      c[stagesById[l.id]]++;
    }
    return c;
  }, [loads, stagesById]);

  const stageBadge = (stage: RealStage) => {
    switch (stage) {
      case "bidding":
        return (
          <Badge className="bg-emerald-500/15 text-emerald-500 border-0 text-[10px] uppercase font-bold whitespace-nowrap gap-1.5">
            <span className="relative flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-400" />
            </span>
            Bidding
          </Badge>
        );
      case "awarded":
        return <Badge className="bg-brand-teal/15 text-brand-teal border-0 text-[10px] uppercase font-bold whitespace-nowrap">Awarded</Badge>;
      case "dispatching":
        return <Badge className="bg-sky-500/15 text-sky-600 border-0 text-[10px] uppercase font-bold whitespace-nowrap">Dispatching</Badge>;
      case "in_transit":
        return <Badge className="bg-amber-500/15 text-amber-600 border-0 text-[10px] uppercase font-bold whitespace-nowrap">In Transit</Badge>;
      case "delivered":
        return <Badge className="bg-emerald-500/15 text-emerald-600 border-0 text-[10px] uppercase font-bold whitespace-nowrap">Delivered</Badge>;
      case "closed":
      default:
        return <Badge className="bg-muted text-muted-foreground border-0 text-[10px] uppercase font-bold whitespace-nowrap">Closed</Badge>;
    }
  };

  const archivedBadge = (
    <Badge className="bg-slate-500/15 text-slate-600 border-0 text-[10px] uppercase font-bold whitespace-nowrap">Archived</Badge>
  );

  const FILTER_TABS: { key: Stage; label: string }[] = [
    { key: "all", label: "All" },
    { key: "bidding", label: "Bidding" },
    { key: "awarded", label: "Awarded" },
    { key: "dispatching", label: "Dispatching" },
    { key: "in_transit", label: "In Transit" },
    { key: "delivered", label: "Delivered" },
    { key: "closed", label: "Closed" },
    { key: "archive", label: "Archive" },
  ];

  const emptyMessages: Record<Stage, { title: string; sub: string }> = {
    all: { title: "No loads yet", sub: "Post a load to get started." },
    bidding: { title: "No loads accepting bids", sub: "Post a load to start receiving bids." },
    awarded: { title: "No awarded loads", sub: "Award a winning bid from the Live Room to move loads here." },
    dispatching: { title: "No loads being dispatched", sub: "After awarding, build the dispatch packet to mark the load Ready or Assigned." },
    in_transit: { title: "No loads in transit", sub: "Assigned loads in motion will appear here." },
    delivered: { title: "No delivered loads", sub: "Loads marked delivered or with completed dispatch appear here." },
    closed: { title: "No closed loads", sub: "Expired, canceled, or completed loads appear here." },
    archive: { title: "Nothing archived", sub: "Loads you archive from any tab will show up here." },
  };

  const SortHeader = ({ k, label, className }: { k: SortKey; label: string; className?: string }) => {
    const active = sortKey === k;
    return (
      <button
        type="button"
        onClick={() => toggleSort(k)}
        className={cn("flex items-center gap-1 hover:text-foreground transition-colors w-full text-left", active && "text-foreground font-semibold", className)}
      >
        {label}
        {active ? (sortDir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card px-3 sm:px-6 py-4 sm:py-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-teal/10 shrink-0">
              <Zap className="h-5 w-5 text-brand-teal" />
            </div>
            <div className="min-w-0">
              <DemoHint hintKey="page.lrDirectMyLoads" side="bottom" showIndicator>
                <h1 className="text-xl font-bold text-foreground">My Loads</h1>
              </DemoHint>
              <p className="text-xs sm:text-sm text-muted-foreground">Monitor bids, open the Live Room, and manage your freight</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="gap-2 flex-1 sm:flex-none h-11 sm:h-9"
              onClick={() => navigate("/lr-direct/dispatches/addresses")}
              title="Address Book"
            >
              <BookUser className="h-4 w-4" />
              <span className="hidden md:inline">Address Book</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="gap-2 flex-1 sm:flex-none h-11 sm:h-9"
              onClick={() => navigate("/lr-direct/network-settings")}
            >
              <Users className="h-4 w-4" />
              <span className="hidden xs:inline sm:inline">Private Network</span>
              <span className="xs:hidden sm:hidden">Network</span>
            </Button>
            <Button onClick={() => navigate("/lr-direct/post/start")} className="gap-2 flex-1 sm:flex-none h-11 sm:h-9" style={{ backgroundColor: "hsl(var(--brand-teal))" }}>
              <Plus className="h-4 w-4" />
              Post Load
            </Button>
          </div>
        </div>
      </div>

      {showCreditBanner && (
        <div className="px-3 sm:px-6 pt-4">
          <CreditProfileBanner companyId={companyId!} canEdit={isAdmin} />
        </div>
      )}

      {/* Search + Filter Tabs */}
      <div className="px-3 sm:px-6 pt-4 pb-2 flex flex-col gap-3">
        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <div className="relative w-full sm:max-w-md">
          <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search ref, PU#, city, equipment…"
            className="h-9 pl-8 pr-8 text-sm"
          />
          {searchInput && (
            <button
              type="button"
              onClick={() => setSearchInput("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
          <Button
            variant="outline"
            onClick={handleDownloadCsv}
            disabled={loading || loads.length === 0}
            className="h-9 gap-2 w-full sm:w-auto sm:ml-auto shrink-0"
          >
            <Download className="h-4 w-4" />
            Download CSV
          </Button>
        </div>
        <div className="flex items-center gap-1 overflow-x-auto">
          {FILTER_TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              className={cn(
                "px-3 py-1.5 rounded-md text-xs font-semibold transition-colors shrink-0",
                filter === tab.key
                  ? "bg-brand-teal/10 text-brand-teal"
                  : "text-muted-foreground hover:bg-muted"
              )}
            >
              {tab.label}
              <span className="ml-1.5 text-[10px] opacity-70">{counts[tab.key]}</span>
            </button>
          ))}
        </div>
      </div>


      {/* Content */}
      <div className="px-3 sm:px-6 py-2">
        {loading ? (
          <LoadingTable />
        ) : filteredLoads.length === 0 ? (
          <EmptyState
            title={emptyMessages[filter].title}
            subtitle={emptyMessages[filter].sub}
            showPost={filter === "bidding"}
            onPost={() => navigate("/lr-direct/post/start")}
          />
        ) : (
          <>
            <div className="mb-2 flex items-center justify-between gap-2">
              <p className="text-xs text-muted-foreground font-medium">
                Showing <span className="text-foreground font-semibold">{totalRows === 0 ? 0 : (page - 1) * pageSize + 1}–{Math.min(page * pageSize, totalRows)}</span> of <span className="text-foreground font-semibold">{totalRows}</span> load{totalRows !== 1 ? "s" : ""}
              </p>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-1.5 text-xs text-muted-foreground font-medium">
                  Rows per page
                  <select
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                    className="h-7 rounded-md border border-input bg-background px-1.5 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                  >
                    {PAGE_SIZE_OPTIONS.map((n) => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </label>
                <Pager page={page} totalPages={totalPages} onChange={setPage} />
              </div>
            </div>

            {/* Sticky bulk action bar */}
            {selectedIds.size > 0 && (
              <div className="sticky top-16 z-30 mb-2 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-brand-teal/40 bg-brand-teal/10 px-3 py-2 shadow-sm backdrop-blur">
                <div className="flex items-center gap-2 text-[12px]">
                  <span className="font-semibold text-foreground">
                    {selectedIds.size} selected
                  </span>
                  {selectedAwardedCount > 0 && (
                    <span className="text-muted-foreground">
                      · {selectedAwardedCount} awarded
                    </span>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-[11px] text-muted-foreground hover:text-foreground"
                    onClick={clearSelection}
                    disabled={bulkBusy}
                  >
                    Clear
                  </Button>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 px-2.5 text-[11px] gap-1.5 border-brand-teal/40 text-brand-teal hover:bg-brand-teal/10 hover:text-brand-teal"
                    onClick={() => setBulkArchiveOpen(true)}
                    disabled={bulkBusy || selectedLoads.length === 0}
                  >
                    {selectedAllArchived ? (<><Undo2 className="h-3.5 w-3.5" />Unarchive</>) : (<><Archive className="h-3.5 w-3.5" />Archive</>)}
                  </Button>
                  <TooltipProvider delayDuration={200}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span tabIndex={0} className="inline-flex">
                          <Button
                            variant="outline"
                            size="sm"
                            className={cn(
                              "h-7 px-2.5 text-[11px] gap-1.5",
                              bulkDeleteDisabled
                                ? "border-border bg-muted/30 text-muted-foreground/60 cursor-not-allowed"
                                : "border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive",
                            )}
                            onClick={() => {
                              if (bulkDeleteDisabled) {
                                sonnerToast.info("Awarded loads can't be deleted", { description: bulkDeleteTip });
                                return;
                              }
                              setBulkDeleteOpen(true);
                            }}
                            disabled={bulkBusy}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                            Delete
                          </Button>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="text-[11px] max-w-[240px]">
                        {bulkDeleteTip}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>
            )}

            <div className="hidden md:block bg-card rounded-lg overflow-x-auto border border-border">
              <TooltipProvider delayDuration={200}>
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/40">
                    <TableHead className="w-[36px] px-2 py-1.5">
                      <Checkbox
                        checked={pageAllSelected ? true : pageSomeSelected ? "indeterminate" : false}
                        onCheckedChange={() => togglePageAll()}
                        aria-label="Select all on page"
                      />
                    </TableHead>
                    <TableHead className="w-[90px] px-2.5 py-1.5 text-[11px]"><SortHeader k="load_number" label="Ref" /></TableHead>
                    <TableHead className="px-2.5 py-1.5 text-[11px] border-l border-border"><SortHeader k="origin" label="Origin" /></TableHead>
                    <TableHead className="px-2.5 py-1.5 text-[11px] border-l border-border"><SortHeader k="destination" label="Destination" /></TableHead>
                    <TableHead className="w-[80px] px-2.5 py-1.5 text-[11px] border-l border-border"><SortHeader k="pickup_date" label="Pickup" /></TableHead>
                    <TableHead className="w-[90px] px-2.5 py-1.5 text-[11px] border-l border-border"><SortHeader k="equipment_type" label="Equipment" /></TableHead>
                    <TableHead className="w-[80px] px-2.5 py-1.5 text-[11px] border-l border-border">Visibility</TableHead>
                    <TableHead className="w-[70px] px-2.5 py-1.5 text-[11px] border-l border-border"><SortHeader k="bid_count" label="Bids" /></TableHead>
                    <TableHead className="w-[90px] px-2.5 py-1.5 text-[11px] border-l border-border"><SortHeader k="lowest_bid" label="Lowest Bid" /></TableHead>
                    <TableHead className="w-[70px] px-2.5 py-1.5 text-[11px] border-l border-border">Health</TableHead>
                    <TableHead className="w-[90px] px-2.5 py-1.5 text-[11px] border-l border-border"><SortHeader k="deadline" label="Deadline" /></TableHead>
                    <TableHead className="w-[104px] px-2.5 py-1.5 text-[11px] border-l border-border"><SortHeader k="stage" label="Status" /></TableHead>
                    <TableHead className="w-[360px] px-2.5 py-1.5 text-[11px] border-l border-border">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pagedLoads.map((load, i) => {
                    const stats = bidStats[load.id];
                    const deadline = formatDeadline(load.bid_deadline);
                    const health = getHealthIndicator(stats?.bidCount ?? 0);
                    const stage = stagesById[load.id];
                    return (
                      <TableRow
                        key={load.id}
                        className={cn(
                          "hover:bg-muted/60 transition-colors cursor-pointer",
                          i % 2 === 1 && "bg-muted/20"
                        )}
                        onClick={() => navigate(shipperLoadPath(load, stage))}
                      >
                        <TableCell className="px-2 py-1.5 border-l-0" onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedIds.has(load.id)}
                            onCheckedChange={() => toggleRow(load.id)}
                            aria-label={`Select load ${load.load_number}`}
                          />
                        </TableCell>
                        <TableCell className="px-2.5 py-1.5 font-mono text-[11px] border-l border-border">
                          <span className="text-brand-teal font-semibold">{load.load_number}</span>
                        </TableCell>
                        <TableCell className="px-2.5 py-1.5 text-[12px] font-medium border-l border-border">
                          {load.origin_city}, {load.origin_state}
                        </TableCell>
                        <TableCell className="px-2.5 py-1.5 text-[12px] font-medium border-l border-border">
                          <div className="flex items-center gap-2">
                            <span>{load.destination_city}, {load.destination_state}</span>
                            {load.archived_at && archivedBadge}
                          </div>
                        </TableCell>
                        <TableCell className="px-2.5 py-1.5 text-[12px] border-l border-border">{formatPickupLabel(load.pickup_date)}</TableCell>
                        <TableCell className="px-2.5 py-1.5 text-[12px] border-l border-border">{formatEquipmentType(load.equipment_type, eqData)}</TableCell>
                        <TableCell className="px-2.5 py-1.5 border-l border-border" onClick={(e) => e.stopPropagation()}>
                          <VisibilityBadge mode={load.visibility_mode} />
                        </TableCell>
                        <TableCell className="px-2.5 py-1.5 text-[12px] border-l border-border font-medium">
                          {(() => {
                            const count = stats?.bidCount ?? 0;
                            return (
                              <span className={cn("tabular-nums", count > 0 ? "font-semibold text-brand-teal" : "text-muted-foreground")}>
                                {count}
                              </span>
                            );
                          })()}
                        </TableCell>
                        <TableCell className="px-2.5 py-1.5 text-[13px] font-bold border-l border-border tabular-nums">
                          {stats?.lowestBid ? `$${stats.lowestBid.toLocaleString()}` : "—"}
                        </TableCell>
                        <TableCell className="px-2.5 py-1.5 border-l border-border">
                          <div className="flex items-center gap-1.5">
                            <span className={cn("h-2 w-2 rounded-full flex-shrink-0", health.dotColor)} />
                            <span className={cn("text-[10px] font-medium", health.color)}>{health.label}</span>
                          </div>
                        </TableCell>
                        <TableCell className={cn("px-2.5 py-1.5 text-[12px] border-l border-border", stage === "bidding" && deadline.urgent ? "text-destructive font-semibold" : "text-muted-foreground")}>
                          {stage === "bidding" ? (
                            <>
                              {deadline.urgent && <Clock className="inline h-3 w-3 mr-0.5 -mt-0.5" />}
                              {deadline.text}
                            </>
                          ) : (
                            <span className="text-muted-foreground/60">—</span>
                          )}
                        </TableCell>
                        <TableCell className="px-2.5 py-1.5 border-l border-border">{stageBadge(stage)}</TableCell>
                        <TableCell className="px-2.5 py-1.5 border-l border-border" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center gap-1.5 flex-nowrap">
                            <StageGatedButtons
                              load={load}
                              stage={stage}
                              navigate={navigate}
                              dispatchInfo={dispatchMap[load.id]}
                              shipperId={user?.id}
                              onDispatchCreated={(id) => setDispatchMap((prev) => ({ ...prev, [load.id]: { id, status: prev[load.id]?.status ?? "draft" } }))}
                              bidCount={bidStats[load.id]?.bidCount ?? 0}
                            />
                            <span className="mx-1 h-5 w-px bg-border" />
                            <RowUtilityButtons
                              load={load}
                              hasPendingBids={(bidStats[load.id]?.bidCount ?? 0) > 0}
                              onDeleted={() => setLoads((prev) => prev.filter((l) => l.id !== load.id))}
                              onArchiveChange={(archivedAt) => setLoads((prev) => prev.map((l) => l.id === load.id ? { ...l, archived_at: archivedAt } : l))}
                            />
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              </TooltipProvider>
            </div>

            {/* Mobile card list */}
            <div className="md:hidden space-y-2">
              <TooltipProvider delayDuration={200}>
              {pagedLoads.map((load) => {
                const stats = bidStats[load.id];
                const deadline = formatDeadline(load.bid_deadline);
                const health = getHealthIndicator(stats?.bidCount ?? 0);
                const stage = stagesById[load.id];
                return (
                  <div
                    key={load.id}
                    className="bg-card border border-border rounded-lg p-3 active:bg-muted/40"
                    onClick={() => navigate(shipperLoadPath(load, stage))}
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2 min-w-0 flex-wrap">
                        <span onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedIds.has(load.id)}
                            onCheckedChange={() => toggleRow(load.id)}
                            aria-label={`Select load ${load.load_number}`}
                          />
                        </span>
                        <span className="font-mono text-[11px] font-semibold text-brand-teal">{load.load_number}</span>
                        {load.archived_at && archivedBadge}
                      </div>
                      {stageBadge(stage)}
                    </div>
                    {(load.status === "awarded" || !!load.awarded_bid_id) && (
                      <div className="mb-2 flex items-start gap-1.5 text-[10px] text-muted-foreground bg-muted/40 rounded px-2 py-1">
                        <Info className="h-3 w-3 mt-0.5 flex-shrink-0" />
                        <span>Awarded loads can't be deleted — use <strong>Archive</strong> to hide them.</span>
                      </div>
                    )}
                    <div className="text-sm font-medium text-foreground mb-1">
                      {load.origin_city}, {load.origin_state}
                      <span className="text-muted-foreground mx-1.5">→</span>
                      {load.destination_city}, {load.destination_state}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
                      <span><strong className="text-foreground">{formatPickupLabel(load.pickup_date)}</strong></span>
                      <span>{formatEquipmentType(load.equipment_type, eqData)}</span>
                      <span className="flex items-center gap-1">
                        Visibility: <VisibilityBadge mode={load.visibility_mode} />
                      </span>
                      {(() => {
                        const count = stats?.bidCount ?? 0;
                        return (
                          <span className="flex items-center gap-1">
                            <span className="text-muted-foreground">Bids:</span>
                            <span className={cn("tabular-nums", count > 0 ? "font-semibold text-brand-teal" : "text-muted-foreground")}>
                              {count}
                            </span>
                          </span>
                        );
                      })()}
                      {stats?.lowestBid && (
                        <span>Low: <strong className="text-foreground tabular-nums">${stats.lowestBid.toLocaleString()}</strong></span>
                      )}
                      {stage === "bidding" ? (
                        <span className={cn("flex items-center gap-1", deadline.urgent && "text-destructive font-semibold")}>
                          {deadline.urgent && <Clock className="h-3 w-3" />} {deadline.text}
                        </span>
                      ) : null}
                      <span className="flex items-center gap-1">
                        <span className={cn("h-1.5 w-1.5 rounded-full", health.dotColor)} /> {health.label}
                      </span>
                    </div>
                    <div className="mt-2 pt-2 border-t border-border flex flex-wrap items-center justify-between gap-2" onClick={(e) => e.stopPropagation()}>
                      <StageGatedButtons
                        load={load}
                        stage={stage}
                        navigate={navigate}
                        dispatchInfo={dispatchMap[load.id]}
                        shipperId={user?.id}
                        onDispatchCreated={(id) => setDispatchMap((prev) => ({ ...prev, [load.id]: { id, status: prev[load.id]?.status ?? "draft" } }))}
                        bidCount={bidStats[load.id]?.bidCount ?? 0}
                      />
                      <RowUtilityButtons
                        load={load}
                        hasPendingBids={(bidStats[load.id]?.bidCount ?? 0) > 0}
                        onDeleted={() => setLoads((prev) => prev.filter((l) => l.id !== load.id))}
                        onArchiveChange={(archivedAt) => setLoads((prev) => prev.map((l) => l.id === load.id ? { ...l, archived_at: archivedAt } : l))}
                      />
                    </div>
                  </div>
                );
              })}
              </TooltipProvider>
            </div>


            <div className="mt-3 flex items-center justify-between gap-2">
              <p className="text-xs text-muted-foreground font-medium">
                Page <span className="text-foreground font-semibold">{page}</span> of <span className="text-foreground font-semibold">{totalPages}</span>
              </p>
              <Pager page={page} totalPages={totalPages} onChange={setPage} />
            </div>

          </>
        )}
      </div>

      {/* Bulk archive/unarchive confirmation */}
      <AlertDialog open={bulkArchiveOpen} onOpenChange={setBulkArchiveOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {selectedAllArchived
                ? `Unarchive ${selectedLoads.length} load${selectedLoads.length === 1 ? "" : "s"}?`
                : `Archive ${selectedLoads.length} load${selectedLoads.length === 1 ? "" : "s"}?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {selectedAllArchived
                ? "The selected loads will move back to their normal tabs."
                : "Archived loads move out of the active tabs into Archive. Search still finds them and you can unarchive any time."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkBusy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); executeBulkArchive(selectedAllArchived ? "unarchive" : "archive"); }}
              disabled={bulkBusy}
            >
              {bulkBusy ? "Working…" : selectedAllArchived ? "Unarchive" : "Archive"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk delete confirmation */}
      <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedLoads.length} load{selectedLoads.length === 1 ? "" : "s"}?</AlertDialogTitle>
            <AlertDialogDescription>
              This cannot be undone. Any pending bids on these loads will be canceled and bidders will be notified.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkBusy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); executeBulkDelete(); }}
              disabled={bulkBusy}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {bulkBusy ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function StageGatedButtons({
  load,
  stage,
  navigate,
  dispatchInfo,
  shipperId,
  onDispatchCreated,
  bidCount = 0,
}: {
  load: DirectLoad;
  stage: RealStage;
  navigate: (path: string) => void;
  dispatchInfo?: DispatchInfo;
  shipperId?: string;
  onDispatchCreated?: (dispatchId: string) => void;
  bidCount?: number;
}) {
  const [creatingDispatch, setCreatingDispatch] = useState(false);
  const isAwarded =
    stage === "awarded" ||
    stage === "dispatching" ||
    stage === "in_transit" ||
    stage === "delivered" ||
    load.status === "awarded" ||
    !!load.awarded_bid_id;

  const hasDispatch = !!dispatchInfo?.id;

  // Bid Room: open while bidding AND after an award (it shows the award
  // summary + the "create dispatch" next step there).
  const biddingLive = isBiddingOpen(load) && stage === "bidding";
  // "Live" only when bidding is genuinely open. Otherwise the same button
  // becomes "Bids" — a read-only trip into the bid room to review who bid.
  const roomEnabled = biddingLive || isAwarded || bidCount > 0;
  // Award: reachable WHILE bids are live (that's the point) and after an award.
  // Both go to the bid room — the one place a shipper reviews bids and awards.
  // Post-close awarding stays supported: any load with bids can be awarded.
  const awardEnabled = isAwarded || bidCount > 0;
  const awardTip = isAwarded
    ? "Award complete."
    : biddingLive
    ? "No bids to award yet."
    : "No bids to award — bidding closed.";
  // Dispatch: always available for actionable rows (archived loads are not rendered here).
  const dispatchEnabled = true;

  const handleDispatchClick = async () => {
    if (hasDispatch || !shipperId) {
      navigate(`/lr-direct/dispatches/build/${load.id}`);
      return;
    }
    setCreatingDispatch(true);
    try {
      const id = await ensureDispatch(load.id, shipperId);
      onDispatchCreated?.(id);
      sonnerToast.success("Dispatch created");
      navigate(`/lr-direct/dispatches/build/${load.id}`);
    } catch (e: any) {
      sonnerToast.error(e?.message || "Failed to create dispatch");
    } finally {
      setCreatingDispatch(false);
    }
  };

  const btn = (
    enabled: boolean,
    onClick: () => void,
    Icon: React.ComponentType<{ className?: string }>,
    label: string,
    lockedTip: string,
    highlight?: boolean,
    loading?: boolean,
  ) => {
    const base =
      "h-7 px-2 text-[11px] gap-1 font-semibold border transition-colors whitespace-nowrap";
    const enabledCls = highlight
      ? "border-brand-teal/50 bg-brand-teal/10 text-brand-teal hover:bg-brand-teal/20 ring-2 ring-brand-teal/40"
      : "border-brand-teal/30 text-brand-teal hover:bg-brand-teal/10";
    const disabledCls =
      "border-border bg-muted/30 text-muted-foreground/60 cursor-not-allowed";
    const body = (
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={!enabled || loading}
        onClick={(e) => {
          e.stopPropagation();
          if (!enabled) return;
          onClick();
        }}
        className={cn(base, enabled ? enabledCls : disabledCls)}
      >
        <Icon className="h-3.5 w-3.5" />
        <span>{loading ? "…" : label}</span>
      </Button>
    );
    if (enabled) return body;
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          {/* wrapper span so tooltip works on a disabled button */}
          <span tabIndex={0} className="inline-flex">{body}</span>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-[11px]">{lockedTip}</TooltipContent>
      </Tooltip>
    );
  };

  const dispatchLabel = hasDispatch ? "Dispatch" : "Create Dispatch";
  const DispatchIcon = hasDispatch ? FileText : Wrench;

  return (
    <div className="flex items-center gap-1 flex-wrap md:flex-nowrap">
      {btn(
        roomEnabled,
        () => navigate(`/lr-direct/live/${load.id}`),
        biddingLive ? Radio : ClipboardList,
        biddingLive ? "Live" : "Bids",
        "Bidding closed — no bids received.",
      )}
      {btn(
        awardEnabled,
        () => navigate(`/lr-direct/live/${load.id}`),
        Trophy,
        "Award",
        awardTip,
        stage === "awarded" || (biddingLive && bidCount > 0),
      )}
      {btn(
        dispatchEnabled,
        handleDispatchClick,
        DispatchIcon,
        dispatchLabel,
        "Archived loads can't be edited.",
        dispatchEnabled && !hasDispatch,
        creatingDispatch,
      )}
    </div>
  );
}



function RowUtilityButtons({
  load,
  hasPendingBids,
  onDeleted,
  onArchiveChange,
}: {
  load: DirectLoad;
  hasPendingBids: boolean;
  onDeleted: () => void;
  onArchiveChange: (archivedAt: string | null) => void;
}) {
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const isArchived = !!load.archived_at;
  const isAwarded = load.status === "awarded" || !!load.awarded_bid_id;
  const canModify = !isAwarded;

  const handleEdit = () => {
    if (isAwarded) { sonnerToast.error("Cannot edit awarded load"); return; }
    if (hasPendingBids) {
      const ok = window.confirm("This load has active bids. Brokers who already bid will see your changes. Continue?");
      if (!ok) return;
    }
    navigate(`/lr-direct/post/${load.id}`);
  };

  const handleDelete = async () => {
    if (isAwarded) {
      sonnerToast.info("Awarded loads can't be deleted", {
        description: "This load has been awarded to a broker. Use Archive instead to keep the business record intact.",
      });
      return;
    }
    setDeleting(true);
    const { data, error } = await supabase
      .from("direct_loads" as any)
      .update({ deleted_at: new Date().toISOString(), status: "canceled" })
      .eq("id", load.id)
      .select("id")
      .maybeSingle();
    if (error) { setDeleting(false); sonnerToast.error(error.message); return; }
    if (!data) {
      setDeleting(false);
      sonnerToast.error("Delete failed", { description: "Load not found or you don't have permission." });
      return;
    }
    await supabase
      .from("direct_bids")
      .update({ status: "canceled" })
      .eq("direct_load_id", load.id)
      .eq("status", "pending");
    setDeleting(false);
    setConfirmOpen(false);
    sonnerToast.success("Load deleted");
    onDeleted();
  };

  const handleArchiveToggle = async () => {
    setArchiving(true);
    const nextArchivedAt = isArchived ? null : new Date().toISOString();
    const { data, error } = await supabase
      .from("direct_loads" as any)
      .update({ archived_at: nextArchivedAt })
      .eq("id", load.id)
      .select("id, archived_at")
      .maybeSingle();
    setArchiving(false);
    if (error) { sonnerToast.error(error.message); return; }
    if (!data) {
      sonnerToast.error(isArchived ? "Unarchive failed" : "Archive failed", {
        description: "Load not found or you don't have permission.",
      });
      return;
    }
    setArchiveConfirmOpen(false);
    sonnerToast.success(isArchived ? "Load unarchived" : "Load archived");
    onArchiveChange(nextArchivedAt);
  };

  return (
    <div className="flex items-center gap-1">
      <Button size="sm" variant="ghost" className="h-6 w-6 p-0"
        onClick={(e) => { e.stopPropagation(); handleEdit(); }}
        disabled={!canModify}
        title={isAwarded ? "Cannot edit awarded load" : "Edit load"}>
        <Pencil className="h-3 w-3" />
      </Button>
      {isArchived ? (
        <Button size="sm" variant="ghost"
          className="h-6 w-6 p-0 text-brand-teal hover:text-brand-teal hover:bg-brand-teal/10"
          onClick={(e) => { e.stopPropagation(); handleArchiveToggle(); }}
          disabled={archiving} title="Unarchive load">
          <Undo2 className="h-3 w-3" />
        </Button>
      ) : (
        <Button size="sm" variant="ghost"
          className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
          onClick={(e) => { e.stopPropagation(); setArchiveConfirmOpen(true); }}
          disabled={archiving} title="Archive load">
          <Archive className="h-3 w-3" />
        </Button>
      )}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button size="sm" variant="ghost"
            className={cn(
              "h-6 w-6 p-0",
              isAwarded
                ? "text-muted-foreground/50 hover:text-muted-foreground hover:bg-transparent cursor-help"
                : "text-destructive hover:text-destructive hover:bg-destructive/10",
            )}
            onClick={(e) => {
              e.stopPropagation();
              if (isAwarded) {
                sonnerToast.info("Awarded loads can't be deleted", {
                  description: "This load is awarded to a broker. Use Archive to hide it — the record stays intact.",
                  duration: 5000,
                });
                return;
              }
              setConfirmOpen(true);
            }}
            aria-label={isAwarded ? "Awarded loads can't be deleted — tap to learn why" : "Delete load"}
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-[11px] max-w-[220px]">
          {isAwarded
            ? "Awarded loads can't be deleted. Use Archive instead — the business record stays intact."
            : "Delete load"}
        </TooltipContent>
      </Tooltip>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this load?</AlertDialogTitle>
            <AlertDialogDescription>
              This cannot be undone.
              {hasPendingBids && " Any pending bids will be canceled and bidders will be notified."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleDelete(); }}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {deleting ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={archiveConfirmOpen} onOpenChange={setArchiveConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive this load?</AlertDialogTitle>
            <AlertDialogDescription>
              Load <strong>{load.load_number}</strong> will be hidden from every tab except <strong>Archive</strong>. Search still finds it, and you can unarchive any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={archiving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleArchiveToggle(); }}
              disabled={archiving}>
              {archiving ? "Archiving…" : "Archive"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function _LegacyStageActionButton_Unused({
  load,
  stage,
  navigate,
  hasPendingBids,
  onDeleted,
  onArchiveChange,
}: {
  load: DirectLoad;
  stage: RealStage;
  navigate: (path: string) => void;
  hasPendingBids: boolean;
  onDeleted: () => void;
  onArchiveChange: (archivedAt: string | null) => void;
}) {
  return null;
}


function Pager({ page, totalPages, onChange }: { page: number; totalPages: number; onChange: (p: number) => void }) {
  if (totalPages <= 1) return null;
  // Show up to 5 numbered pages centered around the current page.
  const window = 5;
  let start = Math.max(1, page - Math.floor(window / 2));
  const end = Math.min(totalPages, start + window - 1);
  start = Math.max(1, end - window + 1);
  const pages = Array.from({ length: end - start + 1 }, (_, i) => start + i);
  return (
    <div className="flex items-center gap-1">
      <button
        onClick={() => onChange(Math.max(1, page - 1))}
        disabled={page === 1}
        className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted disabled:opacity-40"
        aria-label="Previous page"
      >
        <ChevronLeft className="h-3.5 w-3.5" />
      </button>
      {start > 1 && (
        <>
          <button onClick={() => onChange(1)} className="inline-flex h-7 min-w-7 px-1.5 items-center justify-center rounded-md text-[11px] font-medium text-muted-foreground hover:bg-muted">1</button>
          {start > 2 && <span className="text-muted-foreground text-[11px] px-0.5">…</span>}
        </>
      )}
      {pages.map((p) => (
        <button
          key={p}
          onClick={() => onChange(p)}
          className={cn(
            "inline-flex h-7 min-w-7 px-1.5 items-center justify-center rounded-md text-[11px] font-medium transition-colors",
            p === page ? "bg-brand-teal text-white" : "text-muted-foreground hover:bg-muted"
          )}
        >
          {p}
        </button>
      ))}
      {end < totalPages && (
        <>
          {end < totalPages - 1 && <span className="text-muted-foreground text-[11px] px-0.5">…</span>}
          <button onClick={() => onChange(totalPages)} className="inline-flex h-7 min-w-7 px-1.5 items-center justify-center rounded-md text-[11px] font-medium text-muted-foreground hover:bg-muted">{totalPages}</button>
        </>
      )}
      <button
        onClick={() => onChange(Math.min(totalPages, page + 1))}
        disabled={page === totalPages}
        className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted disabled:opacity-40"
        aria-label="Next page"
      >
        <ChevronRight className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function EmptyState({ title, subtitle, showPost, onPost }: { title: string; subtitle: string; showPost: boolean; onPost: () => void }) {
  return (
    <div className="text-center py-16">
      <Zap className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
      <p className="text-muted-foreground font-medium">{title}</p>
      <p className="text-sm text-muted-foreground/70 mt-1 mb-4">{subtitle}</p>
      {showPost && (
        <Button onClick={onPost} className="gap-2" style={{ backgroundColor: "hsl(var(--brand-teal))" }}>
          <Plus className="h-4 w-4" /> Post Load
        </Button>
      )}
    </div>
  );
}

function LoadingTable() {
  return (
    <div className="bg-card rounded-lg overflow-hidden border border-border">
      <Table>
        <TableHeader>
          <TableRow>
            {["", "Ref", "Origin", "Destination", "Pickup", "Equipment", "Visibility", "Bids", "Lowest", "Health", "Deadline", "Status", "Actions"].map((h, idx) => (
              <TableHead key={idx} className="px-2.5 py-1.5 text-[11px]">{h}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array.from({ length: 4 }).map((_, i) => (
            <TableRow key={i}>
              {Array.from({ length: 13 }).map((_, j) => (
                <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function VisibilityBadge({ mode }: { mode?: string | null }) {
  if (mode === "private_only") {
    return (
      <Badge className="bg-brand-teal/10 text-brand-teal border-0 text-[9px] uppercase font-bold gap-1">
        <Users className="h-2.5 w-2.5" /> Private
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="border-border text-muted-foreground text-[9px] uppercase font-bold gap-1">
      <Globe className="h-2.5 w-2.5" /> Open
    </Badge>
  );
}

function PromoteToOpenButton({ load, onPromoted }: { load: DirectLoad; onPromoted: () => void }) {
  const [promoting, setPromoting] = useState(false);
  const handle = async () => {
    if (!confirm("Promote this load to the open LR Direct market? All approved brokers will be able to bid. Existing private bids remain valid.")) return;
    setPromoting(true);
    const { error } = await supabase
      .from("direct_loads" as any)
      .update({ visibility_mode: "open_market", private_network_id: null })
      .eq("id", load.id);
    setPromoting(false);
    if (error) {
      sonnerToast.error(error.message);
      return;
    }
    sonnerToast.success("Load promoted to open market");
    onPromoted();
  };
  return (
    <Button
      size="sm"
      variant="outline"
      className="h-7 px-3 text-[11px] gap-1.5 border-brand-teal/40 text-brand-teal hover:bg-brand-teal/10 hover:text-brand-teal"
      onClick={handle}
      disabled={promoting}
    >
      <Globe className="h-3 w-3" /> {promoting ? "Promoting..." : "Promote to Open Market"}
    </Button>
  );
}

function MarkCompletedButton({ load, onCompleted }: { load: DirectLoad; onCompleted: () => void }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const handle = async () => {
    setSaving(true);
    const { error } = await supabase
      .from("direct_loads" as any)
      .update({ status: "completed" })
      .eq("id", load.id);
    setSaving(false);
    if (error) {
      sonnerToast.error(error.message);
      return;
    }
    sonnerToast.success("Load marked as completed");
    setOpen(false);
    onCompleted();
  };

  return (
    <>
      <Button
        size="sm"
        className="h-7 px-3 text-[11px] gap-1.5 font-semibold"
        style={{ backgroundColor: "hsl(var(--brand-teal))" }}
        onClick={() => setOpen(true)}
      >
        <CheckCircle2 className="h-3 w-3" /> Mark Completed
      </Button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mark load as completed?</AlertDialogTitle>
            <AlertDialogDescription>
              Confirm that load <strong>{load.load_number}</strong> has been delivered. It will move to the Closed tab.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handle(); }}
              disabled={saving}
              style={{ backgroundColor: "hsl(var(--brand-teal))" }}
            >
              {saving ? "Saving…" : "Mark Completed"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
